import { useApolloClient } from "@apollo/client";
import { Company } from "@shared/Collections/models/company";
import { usePermissions } from "@shared/PermissionsProvider";
import { useAnalytics } from "components/shared/Analytics";
import { useAuth } from "components/shared/Authentication";
import { useCollections } from "components/shared/Collections";
import { useCustomRouter } from "components/shared/CustomRouter";
import { CC, CCHelpers } from "@bigspring/core-components";
import { getPathnameByRouteKey } from "config/routes";
import { useEffect, useMemo } from "react";
import {
  NavigationMenuOptionLabel,
  NAVIGATION_MENU_OPTIONS,
  REVAMPED_NAVIGATION_MENU_OPTIONS,
  SelectedRule,
  SUPER_ADMIN_MENU_OPTIONS,
  AdminMenuOption,
  RevampedNavItem,
} from "./menuOptions";
import { NavigationMenuView } from "./view";
import { SpringAIFilled, SpringAIOutline } from "./styles";
import { clearPinnedCompanies } from "./components/SuperAdminMenu";
import { FeatureType, Role, MeDocument } from "@gql/generated/graphql";
import { FlagsmithFeatureType } from "@config/flags";
import { getRuntimeConfigOptional } from "lib/runtimeEnvClient";

const ENV = getRuntimeConfigOptional("NEXT_PUBLIC_ENVIRONMENT") || "local";

let envLabel: string | undefined;
if (ENV !== "production") {
  if (ENV === "development") {
    envLabel = "Dev";
  } else {
    envLabel = ENV.charAt(0).toUpperCase() + ENV.toLowerCase().slice(1);
  }
}

type Props = {
  onOpenMenuClick: () => void;
  onCloseMenuClick: () => void;
};

function Navigation({ onOpenMenuClick, onCloseMenuClick }: Props) {
  const { logout, setCurrentTab } = useAuth();
  const { adminTrack } = useAnalytics();
  const {
    companies,
    currentCompany,
    changeCurrentCompany,
    hasCompanyFeature,
    getFlag,
  } = useCollections();
  const { push, replace, pathname } = useCustomRouter();
  const apolloClient = useApolloClient();
  const { hasRole, hasPermission } = usePermissions();

  const isChatEnabled =
    getFlag<string>(FlagsmithFeatureType.AI_CHAT) === "treatment";

  const isAdminNavRevampEnabled =
    getFlag<string>(FlagsmithFeatureType.ADMIN_NAV_REVAMP) === "treatment";

  const isSuperAdmin = hasRole(Role.SuperAdmin);

  const menuOptions = useMemo(() => {
    if (isChatEnabled) {
      if (
        SUPER_ADMIN_MENU_OPTIONS[1][0].text !== NavigationMenuOptionLabel.CHAT
      ) {
        SUPER_ADMIN_MENU_OPTIONS[1].splice(0, 0, {
          text: NavigationMenuOptionLabel.CHAT,
          startIcon: (isSelected: boolean) =>
            isSelected ? <CC.CommunityIcon /> : <CC.TagOutlinedIcon />,
          uri: "chat",
          selectedRule: SelectedRule.STARTS_WITH,
        });
      }
    }

    if (isSuperAdmin) {
      return SUPER_ADMIN_MENU_OPTIONS.reduce<AdminMenuOption[][]>(
        (sections, section) => {
          const sectionOptions = section.reduce<AdminMenuOption[]>(
            (options, option) => {
              if (option.text === NavigationMenuOptionLabel.LOGOUT) {
                option.action = logout;
              }

              return [...options, option];
            },
            []
          );

          return [...sections, sectionOptions];
        },
        []
      );
    }

    const hasAccess = (option: AdminMenuOption) => {
      if (option.permissions?.length) {
        return hasPermission(option.permissions, currentCompany.id as string);
      }

      return true;
    };

    return NAVIGATION_MENU_OPTIONS.reduce<AdminMenuOption[][]>(
      (sections, section) => {
        let filteredSection: AdminMenuOption[] = [];
        if (hasCompanyFeature(FeatureType.AnonymousUsers)) {
          filteredSection = section.reduce<AdminMenuOption[]>(
            (options, option) => {
              if (option.text === NavigationMenuOptionLabel.LOGOUT) {
                option.action = logout;

                return [...options, option];
              } else if (option.showInAnonymousUsers) {
                return [...options, option];
              }

              return options;
            },
            []
          );
        } else {
          filteredSection = section.reduce<AdminMenuOption[]>(
            (options, option) => {
              if (hasAccess(option)) {
                if (option.text === NavigationMenuOptionLabel.LOGOUT) {
                  option.action = logout;
                }

                return [...options, option];
              }

              return options;
            },
            []
          );
        }

        if (!filteredSection.length) {
          return sections;
        }

        return [...sections, filteredSection];
      },
      []
    );
  }, [
    isSuperAdmin,
    logout,
    hasPermission,
    currentCompany.id,
    hasCompanyFeature,
  ]);

  const filteredRevampedMenuOptions = REVAMPED_NAVIGATION_MENU_OPTIONS.reduce<
    RevampedNavItem[]
  >((acc, navItem) => {
    if (navItem.subsections) {
      const filteredSubsections = navItem.subsections.filter((subsection) => {
        if (!subsection.permissions || subsection.permissions.length === 0) {
          return true;
        }

        return hasPermission(
          subsection.permissions,
          currentCompany.id as string
        );
      });

      if (filteredSubsections.length > 0) {
        acc.push({ ...navItem, subsections: filteredSubsections });
      }
    } else {
      if (!navItem.permissions || navItem.permissions.length === 0) {
        acc.push(navItem);
      } else if (
        hasPermission(navItem.permissions, currentCompany.id as string)
      ) {
        acc.push(navItem);
      }
    }

    return acc;
  }, []);

  // Add CurateAI to revamped menu when feature flag is enabled
  if (isChatEnabled) {
    filteredRevampedMenuOptions.unshift({
      text: "CurateAI",
      iconFilled: <SpringAIFilled />,
      iconOutline: <SpringAIOutline />,
      href: "chat",
    });
  }

  /**
   * Returns a tuple where the first element represents the section and the second the option position in it.
   */
  const currentMenuItemIndex = useMemo(() => {
    let result: [number, number] | undefined;
    let sectionIndex = 0;

    for (const section of menuOptions) {
      const optionIndex = section.findIndex((option) => {
        if (!option.uri) {
          return false;
        }

        if (option.selectedRule === SelectedRule.STARTS_WITH) {
          return pathname.startsWith(getPathnameByRouteKey(option.uri));
        }

        if (option.selectedRule === SelectedRule.EQUALS) {
          return pathname === getPathnameByRouteKey(option.uri);
        }

        return false;
      });

      if (optionIndex !== -1) {
        // This is used to set the value of currenTab present in Analytics and DataDog providers during initial render.
        // If we call setCurrentTab directly from here we are gonna get a console error
        // because we are updating a parent component when a child is still beign rendered.
        // After the initial render we use the useEffect below to set the currentTab
        // and we use the currentTab variable in those providers.
        window.sessionStorage.setItem(
          "current-tab",
          menuOptions[sectionIndex][optionIndex].text as string
        );

        result = [sectionIndex, optionIndex];

        break;
      }

      sectionIndex++;
    }

    return result;
  }, [menuOptions, pathname]);

  useEffect(() => {
    if (currentMenuItemIndex) {
      const [section, option] = currentMenuItemIndex;

      setCurrentTab(menuOptions[section][option].text as string);
    }
  }, [currentMenuItemIndex, menuOptions, setCurrentTab]);

  const onCompanyChange = async (company: Company) => {
    const exclude = ["new_auth", "sessionId", "has_seen_tour"];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i) as string;

      if (exclude.indexOf(key) === -1) {
        localStorage.removeItem(key);
      }
    }

    sessionStorage.clear();

    changeCurrentCompany(company.id);

    await apolloClient.clearStore();
    await apolloClient.query({
      query: MeDocument,
      variables: CCHelpers.getTraits("admin"),
      fetchPolicy: "network-only",
      context: { queryDeduplication: false },
    });

    await replace("dashboard", { companyId: company.id });
    window.dispatchEvent(new CustomEvent("companyChange"));
  };

  const onRevampedOptionClick = async (option: RevampedNavItem) => {
    if (option.href) {
      void push(option.href, { companyId: currentCompany.id });
    }
  };

  const onOptionClick = async (option: AdminMenuOption) => {
    const tabsVisible: NavigationMenuOptionLabel[] = [];
    menuOptions.forEach((section) => {
      section.forEach((option) => {
        tabsVisible.push(option.text);
      });
    });

    adminTrack?.navigationMenuOptionSelected({
      tab: option.text as NavigationMenuOptionLabel,
      tabsVisible,
    });

    if (option.uri) {
      void push(option.uri, { companyId: currentCompany.id });

      return;
    }

    option.action?.();
  };

  const selectedOption = useMemo(() => {
    const sectionIndex = currentMenuItemIndex?.[0];
    const optionIndex = currentMenuItemIndex?.[1];

    if (
      typeof sectionIndex !== "undefined" &&
      typeof optionIndex !== "undefined"
    ) {
      return menuOptions[sectionIndex]?.[optionIndex];
    }
  }, [currentMenuItemIndex, menuOptions]);

  return (
    <NavigationMenuView<AdminMenuOption, "superAdminMenu" | "nonSuperAdminMenu">
      as={isSuperAdmin ? "superAdminMenu" : "nonSuperAdminMenu"}
      additionalProps={{
        label: envLabel,
        companies,
        options: menuOptions,
        isNewAdminNav: isAdminNavRevampEnabled,
        revampedMenuOptions: filteredRevampedMenuOptions,
        selectedOption,
        selectedCompany: currentCompany,
        logoutClick: async () => {
          clearPinnedCompanies();
          await logout();
        },
        onCreateCompanyClick: () => push("companyCreate"),
        onRevampedOptionClick,
        onCompanyChange,
        onOptionClick,
        onOpenMenuClick,
        onCloseMenuClick,
      }}
    />
  );
}

export default Navigation;
